/**
 * Plans, paying, and what sits over the page: the plan card, the checkout, a
 * dialog and a toast. Styles in styles/components.css. Amounts arrive already
 * written ("$40", "12 Credits"): the catalogue says what things cost, never
 * these components.
 */

import type { ComponentChildren } from "preact";
import { useEffect, useId, useRef } from "preact/hooks";
import { Icon } from "./icons";

/** A plan, as Subscriptions lists it: its name, its price, its Credits, one line. The current or featured one is drawn in ink. */
export function PlanCard(props: {
  readonly name: string;
  readonly credits: string;
  readonly price: string;
  readonly period: string;
  readonly summary?: string;
  readonly featured?: boolean;
  readonly current?: boolean;
  readonly action: ComponentChildren;
}) {
  const cls = `plan-card${props.featured ? " featured" : ""}${props.current ? " current" : ""}`;
  return (
    <section class={cls} aria-label={`${props.name} plan`}>
      <div class="plan-top">
        <div class="plan-head">
          <h2 class="plan-name">{props.name}</h2>
          {props.current ? <span class="status done">Your plan</span> : null}
        </div>
        <div class="plan-price">
          <b>{props.price}</b>
          <span>{props.period}</span>
        </div>
        <span class="plan-credits">{props.credits}</span>
        {props.summary ? <p class="plan-summary">{props.summary}</p> : null}
      </div>
      <div>{props.action}</div>
    </section>
  );
}

export interface CheckoutLine {
  readonly label: string;
  readonly amount: string;
  /** A deduction, drawn in the quiet green. */
  readonly credit?: boolean;
  /** Optional attribution or detail revealed on hover or click via (i) icon. */
  readonly info?: string;
}

/** The order: its lines, the total under an ink rule, a note, and the one action. */
export function Checkout(props: {
  readonly title: string;
  readonly lines: readonly CheckoutLine[];
  readonly total: CheckoutLine;
  readonly note?: string;
  readonly action: ComponentChildren;
}) {
  return (
    <section class="checkout" aria-label={props.title}>
      <div class="checkout-h">
        <h2>{props.title}</h2>
      </div>
      {props.lines.map((line) => (
        <div key={line.label} class={line.credit ? "line ok" : "line"}>
          <span>
            {line.label}
            {line.info ? (
              <span class="line-info" title={line.info} aria-label={line.info} role="note" tabIndex={0}>
                <Icon name="info" label={line.info} />
              </span>
            ) : null}
          </span>
          <b>{line.amount}</b>
        </div>
      ))}
      <div class="line total">
        <span>{props.total.label}</span>
        <b>{props.total.amount}</b>
      </div>
      {props.note ? <p class="note">{props.note}</p> : null}
      {props.action}
    </section>
  );
}

/**
 * A dialog, on the browser's own <dialog>: shown modally it keeps focus inside
 * itself, closes on Escape and puts focus back where it was. `inline` draws the
 * same panel in place, not modal, for the component sheet.
 */
export function Dialog(props: {
  readonly open: boolean;
  readonly title: string;
  readonly onClose: () => void;
  readonly actions?: ComponentChildren;
  readonly inline?: boolean;
  readonly children: ComponentChildren;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog || props.inline) return;
    if (props.open && !dialog.open) dialog.showModal();
    if (!props.open && dialog.open) dialog.close();
  }, [props.open, props.inline]);
  return (
    <dialog
      ref={ref}
      class={props.inline ? "dialog inline" : "dialog"}
      open={props.inline ? true : undefined}
      aria-labelledby={titleId}
      onClose={props.onClose}
      onCancel={(event) => {
        event.preventDefault();
        props.onClose();
      }}
    >
      <div class="dialog-head">
        <h2 id={titleId}>{props.title}</h2>
        <button type="button" class="icon-btn" aria-label="Close" onClick={props.onClose}>
          <Icon name="close" />
        </button>
      </div>
      <div class="dialog-body">{props.children}</div>
      {props.actions ? <div class="dialog-actions">{props.actions}</div> : null}
    </dialog>
  );
}

/** A short message in the corner, read out politely, gone when dismissed. `inline` draws it in place. */
export function Toast(props: { readonly title: string; readonly detail?: string; readonly onDismiss: () => void; readonly inline?: boolean }) {
  return (
    <div class={props.inline ? "toast inline" : "toast"} role="status">
      <div class="toast-body">
        <b>{props.title}</b>
        {props.detail ? <span>{props.detail}</span> : null}
      </div>
      <button type="button" class="icon-btn" aria-label="Dismiss" onClick={props.onDismiss}>
        <Icon name="close" />
      </button>
    </div>
  );
}

